import React, { useMemo, Dispatch, SetStateAction, useCallback, useRef, useEffect } from 'react'
import { CSSTransition } from 'react-transition-group'

import useFilter from '../../hooks/FilterContext';
import { windowScreenWidth } from '../../utils/ScrollHandler';
import Range from '../FilterItemPriceRange'
import * as S from './styles'

interface IFilterState {
    env?: string;
    brand?: string;
}

interface IFilterItemContentProps {
    filterName: string;
    filterType: 'radio' | any;
    filterItems?: {
        filterItemName: string;
        filterItemValue: string | number[];
        id?: number;
        brands?: {
            filterItemName: string;
            filterItemValue: string;
        }[]
    }[];
    isOpen: boolean;
    filterProperties: IFilterState | any;
    setfilterProperties: Dispatch<SetStateAction<IFilterState>>;
}

const FilterItemContent: React.FC<IFilterItemContentProps> = ({
    filterName,
    isOpen,
    filterType,
    filterItems,
    setfilterProperties,
    filterProperties,
}) => {

    const nodeRef = useRef(null)
    const { setAttributeHandler } = useFilter()

    const onChangeFilterProperties = useCallback((filterItemValue: any) => (
        !filterProperties[filterName] ?
            setfilterProperties((prevState: any) => ({ ...prevState, [filterName]: filterItemValue }))
            :
            setfilterProperties((prevState: any) => ({ ...prevState, [filterName]: prevState[filterName].concat(filterItemValue) }))
    ), [filterName, filterProperties, setfilterProperties])

    const handleRangeChange = useCallback(
        values => setfilterProperties(prevState => ({ ...prevState, [filterName]: values })),
        [filterName, setfilterProperties]
    )

    const priceRange = filterProperties['priceRange'] || [0, 10000];
    const [changeDefaultValueMin, changeDefaultValueMax] = priceRange;

    const hasProprietes = filterProperties[filterName];
    const hasProprietesRef = useRef(hasProprietes);

    useEffect(() => {
        hasProprietesRef.current = hasProprietes;
    }, [hasProprietes]);

    const onToggleFilterProperties = useCallback((value: any, id) => {
        let div = document.getElementById(id);

        if (hasProprietesRef.current && Array.isArray(hasProprietesRef.current) && hasProprietesRef.current.includes(value)) {
            const indice = hasProprietesRef.current.indexOf(value);
            hasProprietesRef.current.splice(indice, 1);

            if (hasProprietesRef.current.length === 0) {
                delete filterProperties[filterName];
            }

            div?.classList.remove("checked");
        }
        else {
            onChangeFilterProperties([value])
            div?.classList.add("checked");
        }
    }, [filterName, filterProperties, onChangeFilterProperties]);

    useEffect(() => {
        if (windowScreenWidth()) {
            setAttributeHandler(filterProperties.search);
            setfilterProperties((prevState: any) => ({ ...prevState, search: prevState.search ? prevState.search : 'todos' }))
        }
    }, [filterProperties.search, setAttributeHandler, setfilterProperties])


    const paramsFilter = useCallback((filterItem, children) => {
        const checked = hasProprietes?.includes(filterItem.filterItemValue)
        const checkedCategory = JSON.stringify(filterProperties[filterName]) === JSON.stringify(`${filterItem.id}/${filterItem.filterItemValue}`)

        switch (filterName) {
            case 'priceRange': {
                return (
                    <Range onChange={handleRangeChange}
                        key={filterItem.filterItemName}
                        min={changeDefaultValueMin} max={changeDefaultValueMax}
                        defaultMinValue={changeDefaultValueMin}
                        defaultMaxValue={changeDefaultValueMax}
                    />
                )
            }
            case 'category': {
                return (
                    <div key={filterItem.filterItemName} >
                        <S.Button
                            onClick={(e: any) => {
                                onToggleFilterProperties(e.target.value, `${filterItem.id}/${filterItem.filterItemValue}`)
                                setfilterProperties((prevState: any) => ({ ...prevState, [filterName]: `${filterItem.id}/${filterItem.filterItemValue}` }))
                            }}
                            style={checkedCategory ? { background: '#DBC7B8', color: '#424242' } : { background: 'transparent' }}
                            type={filterType}
                        >
                            {children}
                        </S.Button>
                    </div>
                )
            }
            case 'search': {
                return (
                    <div key={filterItem.filterItemName} >
                        <S.Button
                            onClick={() => {
                                setfilterProperties((prevState: any) => ({ ...prevState, [filterName]: filterItem.filterItemValue }))
                            }}
                            style={JSON.stringify(filterProperties[filterName]) === JSON.stringify(filterItem.filterItemValue) ? { background: '#DBC7B8', color: '#424242' } : { background: 'transparent' }}
                            type={filterType}
                        >
                            {children}
                        </S.Button>
                    </div>
                )
            }
            default:
                return (
                    <div key={filterItem.filterItemName} >
                        <S.Button
                            className={checked ? 'checked' : ''}
                            id={filterItem.filterItemValue}
                            type={filterType}
                            onClick={(e: any) => onToggleFilterProperties(e.target.value, filterItem.filterItemValue)}
                            value={filterItem.filterItemValue}
                        >{children} </S.Button>
                    </div>
                )
        }

    }, [hasProprietes, filterProperties, filterName, filterType, handleRangeChange, changeDefaultValueMin, changeDefaultValueMax, onToggleFilterProperties, setfilterProperties])

    const filterContent = useMemo(() => {

        return filterItems?.map((filterItem) => {
            switch (filterItem.filterItemValue) {
                case 'lojaoffpremium': {
                    return (
                        <React.Fragment key={filterItem.filterItemValue}>
                            {filterItem.brands?.map(item => (
                                <React.Fragment key={item.filterItemValue}>
                                    <S.Button type={filterType}
                                        onClick={() => {
                                            setfilterProperties((prevState: any) => ({ ...prevState, 'brand': item.filterItemValue }))
                                            setfilterProperties((prevState: any) => ({ ...prevState }))
                                        }}
                                        style={
                                            JSON.stringify(filterProperties.brand) === JSON.stringify(item.filterItemValue) ?
                                                { background: '#DBC7B8', color: '#424242' } : { background: 'transparent' }}
                                    >
                                        {item.filterItemName}
                                    </S.Button>
                                </React.Fragment>
                            ))
                            }
                        </React.Fragment >
                    )
                }

                default:
                    return (
                        paramsFilter(filterItem, filterItem.filterItemName)
                    )
            }
        })
    }, [filterItems, filterProperties.brand, filterType, paramsFilter, setfilterProperties])

    return (
        <CSSTransition nodeRef={nodeRef} in={isOpen} timeout={400} classNames="top_bottom_weak_effect" unmountOnExit>
            <S.Container ref={nodeRef}>
                {filterContent}
            </S.Container>
        </CSSTransition>
    )
}

export default FilterItemContent